import { useEffect, useState } from 'react'
import { Download, Monitor, Smartphone, WifiOff } from 'lucide-react'
import { PrimaryButton } from './PrimaryButton'
import { downloadOfflinePack, getOfflineState, hasWaitingOfflineUpdate, supportsOfflineInstall, type OfflineState } from '../services/offline'
import { isWindowsDesktop } from '../services/runtimePlatform'

export function OfflineInstallCard() {
  const [state, setState] = useState<OfflineState | null>(null)
  const [working, setWorking] = useState(false)
  const [error, setError] = useState('')
  const [updating, setUpdating] = useState(false)
  const isDesktop = isWindowsDesktop()
  const supported = !isDesktop && supportsOfflineInstall()
  useEffect(() => {
    if (!supported) return
    let mounted = true
    const refresh = async () => {
      try {
        const [next, waiting] = await Promise.all([getOfflineState(), hasWaitingOfflineUpdate()])
        if (mounted) { setState(next); setUpdating(waiting) }
      } catch { if (mounted) setError('离线组件暂未准备好，请保持联网后重试。') }
    }
    void refresh()
    const visible = () => { if (document.visibilityState === 'visible') void refresh() }
    document.addEventListener('visibilitychange', visible)
    return () => { mounted = false; document.removeEventListener('visibilitychange', visible) }
  }, [supported])

  async function prepare() {
    setWorking(true); setError('')
    try {
      const result = await downloadOfflinePack(setState)
      setState(result.state)
      if (!result.state.ready) setError(`还差 ${result.state.total - result.state.completed} 个文件。已下载的内容会保留，请联网重试。`)
    } catch (reason) { setError(reason instanceof Error ? reason.message : '下载未完成，请重试。') }
    finally { setWorking(false) }
  }
  const size = state ? `${(state.totalBytes / 1024 / 1024).toFixed(1)} MB` : '当前已提供的静态资源'
  const percent = state?.total ? Math.floor(state.completed / state.total * 100) : 0
  if (isDesktop) return <section className="surface mt-5" aria-labelledby="offline-install-title">
    <div className="flex items-center gap-2"><Monitor size={22} /><h2 id="offline-install-title" className="text-lg font-extrabold">Windows 桌面版</h2></div>
    <div className="mt-4 rounded-2xl bg-garden-50 p-4">
      <div className="flex items-center gap-2 font-bold"><WifiOff size={19} /><span>已有静态素材随 App 内置</span></div>
      <p className="mt-2 text-sm leading-6 text-garden-700">无需下载网页离线包，现有课程声音和图片可直接离线使用。尚未制作的音频仍不可用；内置素材也不代表已经通过人工发音验收。</p>
      <p className="mt-3 text-xs leading-6 text-garden-700">学习记录保存在这台电脑的 App 数据目录，不会自动导入或同步网页版、iPhone 或其他电脑的记录。来源网站和在线 WAV 下载需要联网。</p>
    </div>
  </section>
  return <section className="surface mt-5" aria-labelledby="offline-install-title">
    <div className="flex items-center gap-2"><Smartphone size={22} /><h2 id="offline-install-title" className="text-lg font-extrabold">在 iPhone 主屏幕使用</h2></div>
    <p className="mt-3 text-sm leading-6 text-garden-700">用 Safari 打开小花园 → 分享 → 添加到主屏幕。若出现“作为网页 App 打开”，请开启。无需安装证书、Mac 或付费账号。</p>
    <p className="mt-2 text-xs leading-6 text-garden-700">添加后，先从主屏幕打开，再在这里下载离线包。Safari、主屏幕 App 与未来原生 App 的本地进度不保证自动互通；请固定使用同一个入口。</p>
    <div className="mt-4 rounded-2xl bg-garden-50 p-4">
      <div className="flex items-center gap-2 font-bold"><WifiOff size={19} /><span>{state?.ready ? '本版本已有资源已保存，可离线练习' : '先准备离线练习'}</span></div>
      <p className="mt-2 text-sm leading-6 text-garden-700">仅下载本版本实际存在的课程声音和图片（{size}）。尚未制作的音频不会凭空补齐；已缓存也不代表发音已经人工核验。成人音频来源研究页面与 WAV 下载包仍需联网。</p>
      {supported ? <>
        {state && <p className="mt-3 text-sm tabular-nums" aria-live="polite">已保存 {state.completed} / {state.total} 个文件 · {percent}%</p>}
        <PrimaryButton type="button" onClick={() => { void prepare() }} disabled={working || state?.ready} className="mt-4 flex items-center justify-center gap-2"><Download size={19} />{working ? '正在保存，请保持页面打开…' : state?.ready ? '离线包已准备' : error || state?.completed ? '继续下载离线包' : '下载离线包'}</PrimaryButton>
      </> : <p className="mt-3 text-sm font-bold text-garden-700">离线下载需在 HTTPS 网站的 Safari 中使用。原生封包直接携带现有静态资源，无需此下载步骤。</p>}
      {error && <p role="alert" className="mt-3 text-sm leading-6 text-amber-900">{error}</p>}
      {updating && <p className="mt-3 text-sm leading-6 text-garden-800">新版已准备，不会打断当前练习。结束后请关闭所有小花园页面，再重新打开并下载新版离线包。</p>}
      <p className="mt-3 text-xs leading-6 text-garden-700">下载完成后请在飞行模式下试一次听音。系统可能回收网站存储；清除网站数据会同时影响离线文件与本地学习记录。</p>
    </div>
  </section>
}
